import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { coachRoutePath, isCoachPage } from '~/utils/coach/coachRoutes';

const NAVIGATION_KEY = 'navigation';

/**
 * The origin a candidate path is normalised against. Never emitted and never compared to anything
 * real: what is wanted off it is the parse — the dot-segment removal and separator handling the
 * browser will perform on the same text — and a path has to have an origin to be parsed at all.
 */
const NORMALISATION_ORIGIN = 'https://placeholder.invalid';

/**
 * The route an old link's `?navigation=` was asking for, or null when it names nothing this app
 * serves. Only the catalogue's own names are translated: anything else spelled out as a path would
 * be a `/coach/<whatever>` no page owns, and the shell is a better landing than a 404.
 *
 * A name is not enough on its own, because the value is a path and a path can walk. The gate reads
 * the first segment, so `rewards/../../admin` is admitted as `rewards` and arrives at `/admin`: the
 * page the link was let through under is not the page it lands on. Worse than the landing is the
 * disagreement — the address bar normalises while the router renders what it was handed, so the two
 * show different surfaces and a reload goes wherever the address bar ended up.
 *
 * So the decision is taken on the normalised result rather than on the text of the value: the
 * candidate is turned into the path it will actually mean, and that path has to still be the route
 * the name translated to, or something under it. Judged as text instead, this is a list of
 * spellings to refuse and there is always one more — `..`, the `%2E%2E` a double-encoded link
 * arrives holding, its lowercase twin, a backslash the parser reads as a separator. Containment
 * does not ask how a walk was spelled, only whether it walked, so an encoding it has never seen
 * either normalises out (and is caught) or does not (and goes nowhere).
 *
 * A value that has left the site never reaches any of that: the gate reads a first segment, and
 * `//evil.com`, `https://evil.com` and the backslash spellings of both are `evil.com`, `https:` and
 * text with no separator in it — none of them names one of the seven pages.
 *
 * What is returned is the normalised path itself and not the text it was normalised from, so what
 * the router is handed is already what the browser will settle on.
 */
const routeForNavigation = (navigation: string): string | null => {
  const [page = ''] = navigation.replace(/^\/+/, '').split('/');

  if (!isCoachPage(page)) {
    return null;
  }

  const url = new URL(coachRoutePath(navigation), NORMALISATION_ORIGIN);

  // A value carrying a query or a hash is writing a second one onto the URL it is spliced into,
  // which is a value editing the link rather than naming a page in it. It is asked after the
  // normalisation because a `?` can arrive past the first segment, where the name gate never looks.
  if (url.search !== '' || url.hash !== '') {
    return null;
  }

  const base = coachRoutePath(page);

  return url.pathname === base || url.pathname.startsWith(`${base}/`) ? url.pathname : null;
};

/**
 * A raw query pair's key as `URLSearchParams` reads it, which is the spelling the spend below found
 * the key under: `navig%61tion=` and `navigation=` are one key and only one of them looks like it.
 *
 * Read through the parser itself rather than mirrored in a second decoder, which is the only way the
 * two answers cannot disagree. The `+` it reads as a space and the malformed escape it swaps for the
 * replacement character are its rules, and a hand-rolled copy of them is a copy that drifts — and a
 * pair the two read differently is one that is both spent and carried.
 *
 * Handed the pair with a separator in front of it, because the parser drops a leading `?` as the
 * question mark that opens a query string — and a pair on its own is not the start of one. Given
 * `?navigation=x` bare it answers `navigation`, which is a key the whole query never had: the pair
 * is dropped as the one being spent while the value that was actually spent came from somewhere
 * else, and an unrelated pair the link was carrying is lost.
 */
const pairKey = (pair: string): string => [...new URLSearchParams(`&${pair}`).keys()][0] ?? '';

/**
 * `/dashboard…` → `/coach…`, with the one thing that moved translated and everything else carried
 * across as text.
 *
 * The thing that moved is `?navigation=`, the legacy query key old links still address the shell's
 * surfaces by. Each of them is a route of its own, so a link that says `?navigation=wallet` is asking for
 * `/coach/badges` and is sent there — the query key is spent on arrival rather than riding along
 * inert. A name the catalogue does not know lands on the bare shell, which is where a link nobody
 * can serve should end up.
 *
 * The rest of the query is spliced rather than rebuilt from parsed params on purpose:
 * `?entryTracking=` is base64 JSON and `?selectedEntity=` is an encoded blob, and re-spelling either
 * of them is a way to break a link that was working. Nothing here changes about them except where
 * the URL they are attached to points.
 *
 * Exact up to the router's own normalisation, which is the last hand this string passes through:
 * `navigateTo` gives it to vue-router, and vue-router re-spells the escapes it considers safe in a
 * query value (`%3D%3D` comes out `==`). What it never does is decode-and-re-encode a pair, so the
 * value a reader decodes is the value that was sent — which is what `entryTracking` is parsed from.
 */
export const coachShellRedirectTarget = (fullPath: string): string => {
  const suffix = fullPath.replace(/^[^?#]*/, '');
  const hashAt = suffix.indexOf('#');
  const hash = hashAt === -1 ? '' : suffix.slice(hashAt);
  const search = (hashAt === -1 ? suffix : suffix.slice(0, hashAt)).replace(/^\?/, '');

  // Read through URLSearchParams and carried across as text: only the one key being spent has to be
  // understood, and decoding the others is what would re-spell them. Which is why the pair dropped
  // is chosen by its decoded NAME while its text is what travels — the key is found decoded, so a
  // pair matched by raw text would leave an encoded spelling of it both spent and carried.
  const navigation = new URLSearchParams(search).get(NAVIGATION_KEY);
  const carried = search.split('&').filter(pair => pair !== '' && pairKey(pair) !== NAVIGATION_KEY);

  const base = navigation === null ? COACH_ROUTE_INDEX : (routeForNavigation(navigation) ?? COACH_ROUTE_INDEX);
  const query = carried.length ? `?${carried.join('&')}` : '';

  return `${base}${query}${hash}`;
};
